/**
 * Who may use FoldMyCard, until it has subscriptions (apps/api/src/approval.ts):
 *
 *   WaitingPage   what a signed-in account sees until it is approved - or if it was
 *                 declined, or suspended in the admin portal (pages/admin.tsx)
 *   ApprovePage   the link in the approver's email: one person, two buttons,
 *                 no sign-in needed - the signed link is the key
 */
import { useEffect, useState, type FormEvent } from 'react';
import { api, type Approval } from '../api.ts';
import { signOutAndLeave } from '../idle.tsx';
import { ContactButton } from '../contact.tsx';
import { Link, useLocation } from '../router.tsx';
import { ago, t, type StringKey } from '../strings.ts';
import { ThemeSwitch } from '../theme.tsx';
import { AuthCard } from './auth.tsx';

const LABEL: Record<Approval, StringKey> = {
  pending: 'approvalPending', approved: 'approvalApproved', declined: 'approvalDeclined', suspended: 'statusSuspended',
};

const PROMO_ON = false;
/** A promo code lets a waiting account in at once (apps/api/src/promo.ts). */
function PromoForm({ onApproved }: { onApproved: () => void }) {
  const [code, setCode] = useState('');
  const [state, setState] = useState<{ busy?: boolean; err?: string }>({});
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setState({ busy: true });
    try {
      await api.redeemPromo(code);
      onApproved();
    } catch (err) {
      setState({ err: (err as Error).message });
    }
  };
  return (
    <form className="stack promo-form" onSubmit={submit}>
      <label>{t('promoHave')}
        <input value={code} onChange={(e) => setCode(e.target.value)} autoComplete="off" autoCapitalize="characters" spellCheck={false} required />
        <span className="hint">{t('promoWaitingHint')}</span>
      </label>
      {state.err && <p className="error" role="alert">{state.err}</p>}
      <button disabled={state.busy || !code.trim()}>{t('promoUse')}</button>
    </form>
  );
}

export function WaitingPage({ email, approval, onApproved }: { email: string; approval: Approval; onApproved?: () => void }) {
  const [title, body] = approval === 'suspended' ? [t('suspendedTitle'), t('suspendedBody')]
    : approval === 'declined' ? [t('declinedTitle'), t('declinedBody')]
    : [t('waitingTitle'), t('waitingBody', { email })];
  return (
    <AuthCard title={title}>
      <p>{body}</p>
      {approval === 'suspended' && <p><ContactButton subject={t('suspendedTitle')} /></p>}
      {/* promo codes are built (PromoForm) but switched off for now, at the owner's request (2026-10-18) */}
      {PROMO_ON && approval === 'pending' && onApproved && <PromoForm onApproved={onApproved} />}
      <div className="row spread">
        <button onClick={() => signOutAndLeave()}>{t('signOut')}</button>
        <ThemeSwitch compact />
      </div>
      <p className="small"><Link href="/account" className="button small">{t('account')}</Link></p>
    </AuthCard>
  );
}

export function ApprovePage() {
  const token = useLocation().searchParams.get('token') || '';
  const [who, setWho] = useState<{ name: string; email: string; approval: Approval; createdAt: string } | null>(null);
  const [state, setState] = useState<{ busy?: boolean; done?: Approval; error?: string }>({});

  useEffect(() => {
    api.approvalLink(token).then((r) => setWho(r.user)).catch(() => setState({ error: t('badApprovalLink') }));
  }, [token]);

  const decide = async (approval: 'approved' | 'declined') => {
    setState({ busy: true });
    try {
      await api.decideLink(token, approval);
      setState({ done: approval });
      setWho((w) => (w ? { ...w, approval } : w));
    } catch (e) {
      setState({ error: (e as Error).message });
    }
  };

  return (
    <AuthCard title={t('approveTitle')}>
      {state.error && <p className="error" role="alert">{state.error}</p>}
      {!who && !state.error && <p className="muted">{t('loading')}</p>}
      {who && (
        <>
          <p><strong>{who.name || who.email}</strong><br /><span className="muted">{who.email}</span><br />
            <span className="small muted">{t('signedUpOn', { when: ago(who.createdAt) })} · {t(LABEL[who.approval])}</span></p>
          {state.done ? (
            <p className="ok">{state.done === 'approved' ? t('approvedDone') : t('declinedDone')}</p>
          ) : (
            <>
              <p>{t('approveAsk')}</p>
              <div className="row">
                <button className="primary" disabled={state.busy || who.approval === 'approved'} onClick={() => decide('approved')}>{t('approve')}</button>
                <button disabled={state.busy || who.approval === 'declined'} onClick={() => decide('declined')}>{t('decline')}</button>
              </div>
            </>
          )}
        </>
      )}
      <p className="small"><Link href="/admin/accounts" className="button small">{t('accounts')} →</Link></p>
    </AuthCard>
  );
}
